import { z } from "zod";
// Reuse the canonical Json schema from the shared barrel's first module so
// `export *` from index.ts has exactly one `jsonSchema` symbol.
import { jsonSchema } from "./service-requests";

/**
 * Listings shared contract (Strangler-Fig migration, manifest-6 / Track A8).
 *
 * Single source of truth for the listings PUBLIC + STAFF wire shapes shared by
 * the web SPA (`src/repositories/listings.ts`) and the NestJS slice
 * (`apps/api/src/listings/*`). Shape is **snake_case** so the frontend<->Nest
 * transport is a field-for-field swap (no renames), matching the Supabase
 * generated Rows and the Nest repository `.select()` lists.
 *
 * ⚠️ TOLERANCE RULE (the #1699 silent-no-op lesson): the public marketplace reads
 * embed nested `property` / `unit` relations whose column sets vary by query (the
 * list select differs from the detail select), and the staff reads add more. So
 * relation fields are loose `.passthrough()` records, optional + nullable — a
 * too-strict relation schema would make every Nest call silently fall back to
 * Supabase. Only each row's own columns are pinned. The whole object is
 * `.passthrough()` so a new migration column never rejects a valid payload.
 */

/**
 * Error code of the API's 422 when a listing would go live without a REGA ad
 * licence (`ListingErrors.AD_LICENSE_REQUIRED` in apps/api, which can't import
 * this package at runtime, so the value is pinned there too).
 */
export const LISTING_AD_LICENSE_REQUIRED_CODE = "lst-004";

/** Loose, passthrough relation shape — see the tolerance rule above. */
const relation = z.record(jsonSchema).nullable().optional();

/**
 * A listing row as the marketplace + staff /ads screens consume it. The own-
 * columns set is permissive (`.passthrough()`) because the public list, public
 * detail, and staff selects each project a different subset; pinning only the
 * stable identity + display columns keeps a single schema usable everywhere
 * without forcing a silent Supabase fallback.
 */
export const listingSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    listing_type: z.string(),
    is_active: z.boolean().nullable().optional(),
    created_at: z.string().optional(),
    // Joined relations (property / unit) — loose + optional + nullable.
    property: relation,
    unit: relation,
  })
  .passthrough();

export type Listing = z.infer<typeof listingSchema>;

/**
 * A public project row (with its nested `project_units` relation). Same tolerance
 * rule — the public-projects list and detail selects both pull `*, project_units(*)`,
 * so own-columns stay permissive; the units array pins the cost-to-own P0 fields
 * (always selected uniformly by `PROJECT_UNITS_SELECT` on the Nest side, so they
 * are never query-dependent like the outer project's own columns) and stays
 * `.passthrough()` for the rest.
 */
export const publicProjectSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    project_type: z.string().optional(),
    is_active: z.boolean().nullable().optional(),
    created_at: z.string().optional(),
    project_units: z
      .array(
        z
          .object({
            id: z.string(),
            price: z.number().nullable().optional(),
            down_payment_amount: z.number().nullable().optional(),
            down_payment_percent: z.number().nullable().optional(),
            mortgage_profit_rate_percent: z.number().nullable().optional(),
            mortgage_tenure_years: z.number().int().nullable().optional(),
          })
          .passthrough(),
      )
      .nullable()
      .optional(),
  })
  .passthrough();

export type PublicProject = z.infer<typeof publicProjectSchema>;

/**
 * A project inquiry row (public visitor lead) returned from the submit endpoint.
 * Pinned to its identity columns; passthrough for the rest.
 */
export const projectInquirySchema = z
  .object({
    id: z.string(),
    project_id: z.string(),
    company_id: z.string(),
    status: z.string().nullable().optional(),
  })
  .passthrough();

export type ProjectInquiry = z.infer<typeof projectInquirySchema>;
